import { OPENING_CONFIGURATION_TYPE } from 'app/typings/core/admin/experiences/enum/openingConfiguration';
import type { OpeningConfiguration } from 'app/typings/core/common/openingConfiguration/openingConfiguration';
import type { BasePriceConfiguration } from 'app/typings/core/common/openingConfiguration/priceConfigurationType';
import { PRICE_DIFFERENTIATION_MODE } from 'app/typings/core/common/openingConfiguration/priceConfigurationType';

import type { Experience } from '../ExtranetPackages.types';
import type {
  PricingTotalBreakdown,
  PricingTotals,
} from '../_components/Tables/ExperiencesTable/PricingTotals.types';

export const buildBreakdown = (exps: Experience[]): PricingTotalBreakdown => {
  let oneAdult = 0;
  let twoAdults = 0;
  let childTotal = 0;
  let hasChild = false;

  for (const exp of exps) {
    const qty = exp.configuration?.quantity ?? 1;
    const adultPrice = exp.adultPrice ?? 0;
    const isPerPerson = exp.configuration?.isPerPerson ?? false;

    oneAdult += adultPrice * qty;
    twoAdults += adultPrice * qty * (isPerPerson ? 2 : 1);

    if (isPerPerson && exp.areChildrenAllowed !== false) {
      hasChild = true;

      const childPrice = exp.childPrice ?? exp.adultPrice ?? 0;

      childTotal += childPrice * qty;
    }
  }

  return {
    oneAdult,
    twoAdults,
    extraAdult: twoAdults - oneAdult,
    ...(hasChild ? { extraChild: childTotal } : {}),
  };
};

export const computePricingTotals = (
  allExperiences: Experience[]
): PricingTotals => {
  const perNightExps = allExperiences.filter(
    (e) => e.configuration?.isPerNight
  );

  return {
    firstNight: buildBreakdown(allExperiences),
    additionalNight:
      perNightExps.length > 0 ? buildBreakdown(perNightExps) : undefined,
  };
};

const pickPrices = (
  config: BasePriceConfiguration | undefined,
  isDaytime: boolean
) =>
  isDaytime
    ? { adult: config?.daytimePrice, child: config?.childDaytimePrice }
    : { adult: config?.price, child: config?.childPrice };

export const extractPrices = (
  openingConfiguration: OpeningConfiguration | undefined,
  isDaytime: boolean,
  areChildrenAllowed?: boolean
) => {
  if (!openingConfiguration?.priceConfiguration) {
    return {
      adultPrice: undefined,
      childPrice: undefined,
      isDailyPrice: false,
    };
  }

  const priceConfig = openingConfiguration.priceConfiguration;
  const useDaytimePrices =
    isDaytime &&
    (priceConfig.priceDifferentiationMode ===
      PRICE_DIFFERENTIATION_MODE.WITH_DAYTIME ||
      priceConfig.priceDifferentiationMode ===
        PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME);
  const isConstant = priceConfig.type === OPENING_CONFIGURATION_TYPE.CONSTANT;

  if (isConstant) {
    const { adult: adultPrice, child } = pickPrices(
      priceConfig.configuration,
      useDaytimePrices
    );

    // Si enfants allowed mais pas de prix enfant, utiliser prix adulte
    const childPrice =
      areChildrenAllowed !== false && child === undefined ? adultPrice : child;

    return {
      adultPrice,
      childPrice,
      isDailyPrice: false,
    };
  }

  const getMinPrice = (key: 'adult' | 'child') =>
    Object.values(priceConfig.days ?? {}).reduce<number | undefined>(
      (min, d) => {
        const val = pickPrices(d, useDaytimePrices)[key];

        return val !== undefined && (min === undefined || val < min)
          ? val
          : min;
      },
      undefined
    );

  const adultPrice = getMinPrice('adult');
  let childPrice = getMinPrice('child');

  // Si enfants allowed mais pas de prix enfant, utiliser prix adulte
  if (areChildrenAllowed !== false && childPrice === undefined) {
    childPrice = adultPrice;
  }

  return {
    adultPrice,
    childPrice,
    isDailyPrice: true,
  };
};
